import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "./Avtar/Avatar";
import PaginationHelper from "../helper/PaginationHelper";
import { truncateText } from "../helper/helper";
import { downloadGuestUsers, getGuestUsers } from "../redux/userSpeaker/userSpeakerSlice";
import useDebounce from "../hook/useDebounce";
import { toast } from "react-toastify";

const GuestUsersListLayer = () => {
  const dispatch = useDispatch();

  const {
    guestUsers = [],
    guestTotal = 0,
    guestTotalPages = 1,
    downloadUsersLoading,
  } = useSelector((state) => state.speaker);

  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const debouncedSearch = useDebounce(search, 1000);

  const queryParams = useMemo(() => ({
    page: currentPage,
    limit: entries,
    search: debouncedSearch?.trim() || "",
  }), [currentPage, entries, debouncedSearch]);

  useEffect(() => {
    dispatch(getGuestUsers(queryParams));
  }, [dispatch, queryParams]);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light">Show</span>

          <select
            className="form-select form-select-sm w-auto h-40-px"
            value={entries}
            onChange={(e) => {
              setEntries(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[50, 100, 150, 200].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>

          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-sm h-40-px pe-32"
              style={{ width: 180 }}
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"
            />
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2">
          <input
            type="date"
            className="form-control form-control-sm h-40-px w-auto"
            style={{ maxWidth: 150 }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            className="form-control form-control-sm h-40-px w-auto"
            style={{ maxWidth: 150 }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <button
            className="btn btn-success btn-sm d-flex align-items-center gap-2"
            disabled={downloadUsersLoading}
            onClick={async () => {
              if (!fromDate || !toDate) {
                toast.error("Please select from date and to date");
                return;
              }

              try {
                await dispatch(downloadGuestUsers({ fromDate, toDate })).unwrap();
                toast.success("Guest users exported successfully");
              } catch (err) {
                toast.error(err?.message || "Export failed");
              }
            }}
          >
            <Icon icon="mdi:file-excel-outline" />
            {downloadUsersLoading ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>

      <div className="card-body p-12">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Code</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Designation</th>
              </tr>
            </thead>

            <tbody>
              {guestUsers?.map((user, index) => (
                <tr key={user.id || index}>
                  <td>{(currentPage - 1) * entries + index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar
                        name={user.fullName || user.name}
                        image={user.profilePicture}
                        uniqueKey={user.id || user.email}
                        size={28}
                      />
                      <span>{truncateText(user.fullName || user.name, 20)}</span>
                    </div>
                  </td>
                  <td>
                    <span className="text-md text-secondary-light">
                      {truncateText(user.email, 24)}
                    </span>
                  </td>
                  <td>
                    <span className="text-md text-secondary-light">
                      {user.countryCode ?? "-"}
                    </span>
                  </td>
                  <td>
                    <span className="text-md text-secondary-light">
                      {truncateText(user.phone, 20)}
                    </span>
                  </td>
                  <td>
                    <span className="text-md text-secondary-light">
                      {truncateText(user?.Company?.name || user.companyName || user.company, 24)}
                    </span>
                  </td>
                  <td>
                    <span className="text-md text-secondary-light">
                      {truncateText(user.designation, 24)}
                    </span>
                  </td>
                </tr>
              ))}

              {guestUsers.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-24 text-secondary-light">
                    <Icon icon="mdi:account-off-outline" width="32" />
                    <p className="mb-0 mt-8">No guest users found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <PaginationHelper
          currentPage={currentPage}
          totalPages={guestTotalPages}
          onPageChange={setCurrentPage}
          totalItems={guestTotal}
          pageSize={entries}
        />
      </div>
    </div>
  );
};

export default GuestUsersListLayer;
